import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Alert, Input, Select, message } from "antd";
import { ChevronDown, ChevronUp, Filter } from "react-feather";
import { useSearchParams } from "react-router-dom";

import CustomDatePicker from "../../CommonComponents/CustomDatePicker/CustomDatePicker";
import SGPRiskTable from "./SGPRiskTable";
import AppContext from "../../../context/AppContext";
import { DATE_RANGE, SIDER_TABS } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  useSgpRiskList,
  useSgpRiskSports,
  useUpdateSgpRiskExposureControls,
} from "../../../queries/sportsbook/sgpRisk";
import { useDebouncedSearchQuery } from "../../../hooks/useDebouncedSearchQuery";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import {
  parseSgpRiskBetsListSearchParams,
  resolveReportingDatesFromIso,
} from "./sgpRiskNavigation";

import styles from "./SGPRisk.module.css";

const SEARCH_DEBOUNCE_MS = 300;

const INITIAL_FILTERS = {
  minLegs: "",
  sport: null,
  minSettledPayout: "",
  minPL: "",
  minROI: "",
};

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT = {
  sortBy: "exposure_used_percentage",
  sortOrder: "desc",
};

function parseOptionalNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseControlNumber(value) {
  if (value === "" || value == null || value === "-") return null;
  const normalized = String(value).replace(/[^0-9.-]/g, "").trim();
  if (!normalized || normalized === "-") return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function SGPRisk() {
  usePageTitle(AppStrings.SPORTSBOOK_SGP_RISK);
  const { setActiveSidebarTab } = useContext(AppContext);
  const currencySymbol = useCurrencySymbol();
  const [searchParams] = useSearchParams();
  const routeDateContext = useMemo(
    () => parseSgpRiskBetsListSearchParams(searchParams),
    [searchParams],
  );
  const [messageApi, contextHolder] = message.useMessage();
  const [searchValue, setSearchValue] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortState, setSortState] = useState(DEFAULT_SORT);
  const [reportingDates, setReportingDates] = useState(() =>
    resolveReportingDatesFromIso(
      routeDateContext.startDate,
      routeDateContext.endDate,
    ),
  );

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.SPORTSBOOK, "SPORTSBOOK_SGP_RISK");
  }, [setActiveSidebarTab]);

  const handleReportingRangeQuery = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
    setCurrentPage(1);
  };

  const { effectiveSearchQuery } = useDebouncedSearchQuery(searchValue, {
    delayMs: SEARCH_DEBOUNCE_MS,
    minLength: 1,
    treatEmptyAsCleared: true,
  });

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  const isFiltersDirty = useMemo(
    () =>
      Object.keys(INITIAL_FILTERS).some(
        (key) => filters[key] !== INITIAL_FILTERS[key],
      ),
    [filters],
  );

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setCurrentPage(1);
    messageApi.success(AppStrings.FILTERS_CLEARED);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
  };

  const handleSortChange = useCallback((nextSortState) => {
    setSortState(nextSortState);
    setCurrentPage(1);
  }, []);

  const sportOptionsQuery = useSgpRiskSports();
  const sportOptions = useMemo(
    () =>
      (sportOptionsQuery.data ?? []).map((sport) => ({
        label: sport.title,
        value: sport.id,
      })),
    [sportOptionsQuery.data],
  );

  const listQueryParams = useMemo(
    () => ({
      start_date: reportingDates?.[0]?.startOf("day")?.toISOString(),
      end_date: reportingDates?.[1]?.endOf("day")?.toISOString(),
      page: currentPage,
      page_size: pageSize,
      search_term: effectiveSearchQuery.trim() || null,
      min_legs: parseOptionalNumber(appliedFilters.minLegs),
      sport_ids: appliedFilters.sport ? [appliedFilters.sport] : null,
      min_settled_payout: parseOptionalNumber(appliedFilters.minSettledPayout),
      min_pl: parseOptionalNumber(appliedFilters.minPL),
      min_roi: parseOptionalNumber(appliedFilters.minROI),
      sort_by: sortState.sortBy,
      sort_order: sortState.sortOrder,
    }),
    [appliedFilters, currentPage, pageSize, reportingDates, effectiveSearchQuery, sortState],
  );
  const listQuery = useSgpRiskList(listQueryParams);
  const exposureControlsMutation = useUpdateSgpRiskExposureControls();
  const betsListDateRange = useMemo(
    () => ({
      startDate: reportingDates?.[0]?.startOf("day")?.toISOString() ?? "",
      endDate: reportingDates?.[1]?.endOf("day")?.toISOString() ?? "",
    }),
    [reportingDates],
  );
  const dataWarnings = listQuery.data?.data_warnings ?? [];
  const isPaginating = listQuery.isFetching && listQuery.isPlaceholderData;
  const isTableLoading = listQuery.isLoading || isPaginating;
  const shouldHoldTableRows = listQuery.isLoading;
  const tableRows = shouldHoldTableRows ? [] : (listQuery.data?.rows ?? []);
  const tableTotalRows = shouldHoldTableRows
    ? 0
    : (listQuery.data?.pagination?.total_results ?? 0);

  const handleExposureControlSave = useCallback(
    async (record, updates) => {
      try {
        const payload = {
          sgp_id: record.sgpId,
          sport_id: record.sportId,
          league_id: record.leagueId || null,
        };

        if (Object.prototype.hasOwnProperty.call(updates, "pendingExpLimit")) {
          payload.exposure_limit = parseControlNumber(updates.pendingExpLimit);
        }

        if (Object.prototype.hasOwnProperty.call(updates, "sgpAdjustment")) {
          payload.sgp_adjustment = parseControlNumber(updates.sgpAdjustment);
        }

        await exposureControlsMutation.mutateAsync(payload);
        messageApi.success(AppStrings.SGP_RISK_EXPOSURE_CONTROLS_UPDATED);
      } catch (error) {
        messageApi.error(AppStrings.SGP_RISK_EXPOSURE_CONTROLS_UPDATE_ERROR);
        throw error;
      }
    },
    [exposureControlsMutation, messageApi],
  );

  const handleExposureControlClear = useCallback(
    async (record) => {
      try {
        await exposureControlsMutation.mutateAsync({
          sgp_id: record.sgpId,
          sport_id: record.sportId,
          league_id: record.leagueId || null,
          exposure_limit: null,
        });
        messageApi.success(AppStrings.SGP_RISK_EXPOSURE_CONTROLS_UPDATED);
      } catch (error) {
        messageApi.error(AppStrings.SGP_RISK_EXPOSURE_CONTROLS_UPDATE_ERROR);
        throw error;
      }
    },
    [exposureControlsMutation, messageApi],
  );

  const handleSgpAdjustmentClear = useCallback(
    async (record) => {
      try {
        await exposureControlsMutation.mutateAsync({
          sgp_id: record.sgpId,
          sport_id: record.sportId,
          league_id: record.leagueId || null,
          sgp_adjustment: null,
        });
        messageApi.success(AppStrings.SGP_RISK_EXPOSURE_CONTROLS_UPDATED);
      } catch (error) {
        messageApi.error(AppStrings.SGP_RISK_EXPOSURE_CONTROLS_UPDATE_ERROR);
        throw error;
      }
    },
    [exposureControlsMutation, messageApi],
  );

  return (
    <>
      <div className={styles["sgp-risk-page"]} data-testid="sgp-risk">
        {contextHolder}
        <div className={styles["sgp-risk-header"]}>
          <div className={styles["sgp-risk-header-actions"]}>
            <div
              className={styles["sgp-risk-date-picker"]}
              data-testid="sgp-risk-date-picker"
            >
              <CustomDatePicker
                dates={reportingDates}
                setDates={setReportingDates}
                getData={handleReportingRangeQuery}
                showPresets
                startRange={DATE_RANGE.LAST_7_DAYS}
              />
            </div>
            <button
              type="button"
              className={styles["sgp-risk-download-button"]}
              aria-label="Download"
              title="CSV download is unavailable until the export API is available."
              disabled
            >
              <DownloadOutlined />
            </button>
          </div>
        </div>
        <div className={styles["sgp-risk-search-bar"]}>
          <div className={styles["sgp-risk-search-wrapper"]}>
            <Input
              placeholder="Search"
              prefix={
                <SearchOutlined
                  style={{ fontSize: "16px", color: "var(--grey-400)" }}
                />
              }
              value={searchValue}
              onChange={handleSearchChange}
              className={styles["sgp-risk-search-input"]}
              allowClear
            />
          </div>

          <div className={styles["sgp-risk-search-divider"]} />

          <button
            type="button"
            className={styles["sgp-risk-filter-button"]}
            data-testid="sgp-risk-filter-toggle"
            aria-label="Toggle filters"
            aria-expanded={showFilters}
            onClick={() => setShowFilters((current) => !current)}
          >
            <Filter className={styles["sgp-risk-filter-icon"]} size={18} />
            {showFilters ? (
              <ChevronUp className={styles["sgp-risk-filter-icon"]} size={16} />
            ) : (
              <ChevronDown className={styles["sgp-risk-filter-icon"]} size={16} />
            )}
          </button>
        </div>

        {showFilters && (
          <div className={styles["sgp-risk-filters-panel"]} data-testid="sgp-risk-filters-panel">
            <div className={styles["sgp-risk-filters-grid"]}>
              <div className={styles["sgp-risk-filter-field"]}>
                <label className={styles["sgp-risk-filter-label"]} htmlFor="sgp-risk-min-legs">
                  Min # of Legs
                </label>
                <Input
                  id="sgp-risk-min-legs"
                  placeholder={AppStrings.SGP_RISK_MIN_LEGS_PLACEHOLDER}
                  value={filters.minLegs}
                  onChange={(event) => updateFilter("minLegs", event.target.value)}
                  className={styles["sgp-risk-filter-input"]}
                />
              </div>

              <div className={styles["sgp-risk-filter-field"]}>
                <label className={styles["sgp-risk-filter-label"]} htmlFor="sgp-risk-sport">
                  {AppStrings.SPORT}
                </label>
                <Select
                  id="sgp-risk-sport"
                  placeholder={AppStrings.SGP_RISK_SPORT_PLACEHOLDER}
                  value={filters.sport}
                  onChange={(value) => updateFilter("sport", value)}
                  options={sportOptions}
                  loading={sportOptionsQuery.isLoading}
                  status={sportOptionsQuery.isError ? "error" : undefined}
                  aria-invalid={sportOptionsQuery.isError ? "true" : undefined}
                  className={styles["sgp-risk-filter-select"]}
                  allowClear
                />
              </div>

              <div className={styles["sgp-risk-filter-field"]}>
                <label
                  className={styles["sgp-risk-filter-label"]}
                  htmlFor="sgp-risk-min-settled-payout"
                >
                  Min Settled Payout
                </label>
                <Input
                  id="sgp-risk-min-settled-payout"
                  placeholder={AppStrings.SGP_RISK_MIN_SETTLED_PAYOUT_PLACEHOLDER}
                  value={filters.minSettledPayout}
                  onChange={(event) =>
                    updateFilter("minSettledPayout", event.target.value)
                  }
                  className={styles["sgp-risk-filter-input"]}
                />
              </div>

              <div className={styles["sgp-risk-filter-field"]}>
                <label className={styles["sgp-risk-filter-label"]} htmlFor="sgp-risk-min-pl">
                  Min P/L
                </label>
                <Input
                  id="sgp-risk-min-pl"
                  placeholder={AppStrings.SGP_RISK_MIN_PL_PLACEHOLDER}
                  value={filters.minPL}
                  onChange={(event) => updateFilter("minPL", event.target.value)}
                  className={styles["sgp-risk-filter-input"]}
                />
              </div>

              <div className={styles["sgp-risk-filter-field"]}>
                <label className={styles["sgp-risk-filter-label"]} htmlFor="sgp-risk-min-roi">
                  Min ROI%
                </label>
                <Input
                  id="sgp-risk-min-roi"
                  placeholder={AppStrings.SGP_RISK_MIN_ROI_PLACEHOLDER}
                  value={filters.minROI}
                  onChange={(event) => updateFilter("minROI", event.target.value)}
                  className={styles["sgp-risk-filter-input"]}
                />
              </div>
            </div>

            <div className={styles["sgp-risk-filter-actions"]}>
              <button
                type="button"
                className={[
                  styles["sgp-risk-filter-action"],
                  styles["sgp-risk-filter-action--clear"],
                ].join(" ")}
                data-testid="sgp-risk-clear-filters"
                onClick={handleClearFilters}
                disabled={!isFiltersDirty}
              >
                {AppStrings.CLEAR_FILTERS}
              </button>
              <button
                type="button"
                className={[
                  styles["sgp-risk-filter-action"],
                  styles["sgp-risk-filter-action--apply"],
                ].join(" ")}
                data-testid="sgp-risk-apply-filters"
                onClick={handleApplyFilters}
              >
                {AppStrings.APPLY}
              </button>
            </div>
          </div>
        )}

        {listQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Unable to load SGP risk data."
            style={{ marginBottom: 16 }}
          />
        )}
        {dataWarnings.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Some exposure limit data may be stale."
            description={dataWarnings.join(", ")}
            style={{ marginBottom: 16 }}
          />
        )}

        <SGPRiskTable
          currencySymbol={currencySymbol}
          rows={tableRows}
          totalRows={tableTotalRows}
          loading={isTableLoading}
          betsListDateRange={betsListDateRange}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          onExposureControlSave={handleExposureControlSave}
          onExposureControlClear={handleExposureControlClear}
          onSgpAdjustmentClear={handleSgpAdjustmentClear}
          sortBy={sortState.sortBy}
          sortOrder={sortState.sortOrder}
          onSortChange={handleSortChange}
        />
      </div>
    </>
  );
}

export default SGPRisk;
